import { startAnalyticsSession, trackAnalyticsEvent, endAnalyticsSession, BASE_URL } from '../api';

const VISITOR_KEY = 'fc_visitor_id';
const SESSION_KEY = 'fc_session_id';
const SESSION_STARTED_KEY = 'fc_session_started';

const genId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Visitor id — persists across visits (localStorage)
export const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) { id = genId(); localStorage.setItem(VISITOR_KEY, id); }
    return id;
  } catch { return genId(); }
};

// Session id — new per tab/browser session (sessionStorage)
export const getSessionId = () => {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) { id = genId(); sessionStorage.setItem(SESSION_KEY, id); }
    return id;
  } catch { return genId(); }
};

// ── Session lifecycle ────────────────────────────────────────
export const startSession = async () => {
  try {
    if (sessionStorage.getItem(SESSION_STARTED_KEY)) return; // already started this tab session
    await startAnalyticsSession({
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      landing_page: window.location.pathname,
      screen_width: window.screen?.width,
      screen_height: window.screen?.height,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      referrer: document.referrer || null,
    });
    sessionStorage.setItem(SESSION_STARTED_KEY, '1');
  } catch {
    // analytics must never break the storefront
  }
};

// sendBeacon can't go through axios (page is unloading), so this one still
// needs a raw fetch — kept minimal and isolated to just this call.
export const endSessionBeacon = (exitPage) => {
  try {
    if (!navigator.sendBeacon) return;
    const blob = new Blob(
      [JSON.stringify({ session_id: getSessionId(), exit_page: exitPage || window.location.pathname })],
      { type: 'application/json' }
    );
    navigator.sendBeacon(BASE_URL +'/analytics/session/end', blob);
  } catch {
    // silent
  }
};

// ── Event tracking — safe to call from any component ────────
export const trackEvent = (event_type, extra = {}) => {
  trackAnalyticsEvent({
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    event_type,
    page_url: window.location.pathname,
    ...extra,
  }).catch(() => {
    // never let analytics failures surface to the shopper
  });
};

export const trackPageView = (pageName, pageUrl) => {
  trackEvent('PAGE_VIEW', { page_name: pageName, page_url: pageUrl });
};